// src/components/CreditHistory.tsx
// Verbruiksoverzicht voor de gebruiker: credit_log gegroepeerd per blog,
// uitklapbaar naar losse acties. Plain NL, geen providernamen of euro's.
import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { ChevronDown, History } from 'lucide-react';
import {
  groupCreditLog,
  actionLabel,
  type CreditLogRow,
  type CreditGroup,
} from '../lib/creditHistory';

interface CreditHistoryProps {
  userId: string;
}

const MONTHS_BACK = 3;

const dateFmtSameYear = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long' });
const dateFmtOtherYear = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function formatGroupDate(iso: string): string {
  const date = new Date(iso);
  return date.getFullYear() === new Date().getFullYear()
    ? dateFmtSameYear.format(date)
    : dateFmtOtherYear.format(date);
}

export function CreditHistory({ userId }: CreditHistoryProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [groups, setGroups] = useState<CreditGroup[] | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(6);
  const hasFetchedRef = useRef(false);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const since = new Date();
      since.setMonth(since.getMonth() - MONTHS_BACK);

      const { data: logRows, error } = await supabase
        .from('credit_log')
        .select('id, session_id, action_type, credits_used, success, created_at')
        .eq('user_id', userId)
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) throw error;
      const rows = (logRows ?? []) as CreditLogRow[];

      const sessionIds = [...new Set(rows.map((r) => r.session_id).filter(Boolean))] as string[];
      const sessionTitles: Record<string, string> = {};
      if (sessionIds.length > 0) {
        const { data: sessions } = await supabase
          .from('sessions')
          .select('id, selected_title')
          .in('id', sessionIds);
        for (const s of sessions ?? []) {
          if (s.selected_title) sessionTitles[s.id] = s.selected_title;
        }
      }

      setGroups(groupCreditLog(rows, sessionTitles));
    } catch (err) {
      console.error('Error loading credit history:', err);
      hasFetchedRef.current = false;
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!open || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    void loadHistory();
  }, [open, loadHistory]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-left group"
      >
        <h2 className="text-lg font-bold text-[#231F20] flex items-center gap-2">
          <History className="w-5 h-5 text-gray-700" aria-hidden="true" />
          Verbruik
        </h2>
        <ChevronDown
          aria-hidden="true"
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 group-hover:text-gray-600 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="mt-4">
          {loading && <p className="text-sm text-gray-500">Verbruik laden…</p>}

          {!loading && loadError && (
            <div className="text-sm">
              <p className="text-gray-600">Het verbruik kon niet worden geladen.</p>
              <button
                onClick={() => {
                  hasFetchedRef.current = true;
                  void loadHistory();
                }}
                className="mt-2 font-medium text-[#FF6B35] hover:underline"
              >
                Opnieuw proberen
              </button>
            </div>
          )}

          {!loading && !loadError && groups && groups.length === 0 && (
            <p className="text-sm text-gray-500">
              Nog geen verbruik in de afgelopen {MONTHS_BACK} maanden.
            </p>
          )}

          {!loading && !loadError && groups && groups.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {groups.slice(0, visibleCount).map((g) => {
                const isExpanded = expandedKey === g.key;
                const hasDetails = g.rows.length > 1;

                const rowContent = (
                  <>
                    <div className="min-w-0 flex items-center gap-2">
                      {hasDetails && (
                        <ChevronDown
                          aria-hidden="true"
                          className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-[#231F20] truncate">
                          {g.title}
                          {g.key.startsWith('los-') && g.rows.length > 1 ? ` (${g.rows.length}x)` : ''}
                        </p>
                        <p className="text-xs text-gray-500">{formatGroupDate(g.latestAt)}</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-[#231F20] whitespace-nowrap">
                      {g.totalCredits.toLocaleString('nl-NL')} credits
                    </span>
                  </>
                );

                return (
                  <li key={g.key}>
                    {hasDetails ? (
                      <button
                        onClick={() => setExpandedKey(isExpanded ? null : g.key)}
                        aria-expanded={isExpanded}
                        className="w-full flex items-center justify-between gap-3 py-3 px-2 -mx-2 rounded-lg text-left transition-colors hover:bg-gray-50"
                      >
                        {rowContent}
                      </button>
                    ) : (
                      <div className="flex items-center justify-between gap-3 py-3">
                        {rowContent}
                      </div>
                    )}

                    {isExpanded && hasDetails && (
                      <ul className="pb-3 pl-8 pr-2 space-y-1.5">
                        {g.rows.map((r) => (
                          <li
                            key={r.id}
                            className="flex items-center justify-between text-sm text-gray-600"
                          >
                            <span>{actionLabel(r.action_type)}</span>
                            <span>{r.credits_used.toLocaleString('nl-NL')}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {!loading && !loadError && groups && groups.length > visibleCount && (
            <div className="pt-2 text-center border-t border-gray-100">
              <button
                onClick={() => setVisibleCount((c) => c + 10)}
                className="mt-1 text-sm font-medium text-[#FF6B35] hover:underline"
              >
                Toon meer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
